import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../utils/analytics';
import LanguageSwitcher from '../components/LanguageSwitcher';
import AiCompanion from '../components/AiCompanion';
import EmailSubscribeBox from '../components/EmailSubscribeBox';
import './PublicChannel.css';

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const seconds = Math.floor((new Date() - new Date(dateStr)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function PublicChannelBrowse() {
  const { user } = useAuth();
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCategories, setOpenCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    document.title = 'Belong — You\'re Not Alone. Talk About It Here.';
    fetchData();
    trackEvent('public_browse_view');
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get('/api/public/channels');
      const groups = res.data.groups || [];
      setCategoryGroups(groups);
      const expanded = {};
      groups.forEach(g => { expanded[g.category_id] = true; });
      setOpenCategories(expanded);
    } catch (err) {
      console.error('Error fetching public data:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (id) => {
    setOpenCategories(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Post search: debounced search when searchTerm is 3+ chars
  useEffect(() => {
    if (searchTerm.trim().length >= 3) {
      const timer = setTimeout(async () => {
        try {
          const res = await axios.get(`/api/public/channels/search?q=${encodeURIComponent(searchTerm.trim())}`);
          setSearchResults(res.data.posts || []);
        } catch (e) { setSearchResults([]); }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  // Client-side filter: match against category name, channel name, and slug
  const term = searchTerm.toLowerCase().trim();
  const filteredGroups = term
    ? categoryGroups
        .map(group => {
          // If category name matches, show all its channels
          if (group.category_display.toLowerCase().includes(term)) return group;
          // Otherwise filter channels within the group
          const matched = group.channels.filter(ch =>
            ch.name.toLowerCase().includes(term) || ch.slug.toLowerCase().includes(term)
          );
          return matched.length > 0 ? { ...group, channels: matched } : null;
        })
        .filter(Boolean)
    : categoryGroups;

  const totalCommunities = categoryGroups.reduce((s, g) => s + g.channels.length, 0);
  const totalPosts = categoryGroups.reduce((s, g) => s + g.channels.reduce((s2, c) => s2 + c.post_count, 0), 0);

  return (
    <div className="public-channel-page">
      {/* ── Nav ── */}
      <nav className="public-channel-top-bar" aria-label="Main navigation">
        <Link to="/" className="public-channel-logo">
          <img src="/belong.png" alt="Belong" />
          <span>Belong</span>
        </Link>
        <div className="public-channel-auth-links">
          <LanguageSwitcher />
          {user ? (
            <Link to="/dashboard" className="public-auth-btn">← Dashboard</Link>
          ) : (
            <>
              <Link to="/login" className="public-auth-link">Sign In</Link>
              <Link to="/register" className="public-auth-btn">Join Free</Link>
            </>
          )}
        </div>
      </nav>

      {/* ── Hero (visitors only) ── */}
      {!user && (
      <div className="public-channel-hero">
        <h1>You're not alone.<br />Find people who understand.</h1>
        <p className="public-hero-subtitle">
          Belong is a safe, anonymous community for people with invisible identities — ADHD, chronic illness, neurodivergence, cultural background, and more. Read real discussions, connect with people who share your experience, and build genuine relationships at your own pace.
        </p>
        <img 
          src="/images/undraw_coffee-with-friends_ocg2.svg" 
          alt="" 
          style={{ maxWidth: '280px', width: '100%', margin: '28px auto 4px', display: 'block', opacity: 0.85 }}
          onError={(e) => e.target.style.display = 'none'}
        />
        <div className="hp-hero-actions">
          <a href="#communities" className="public-hero-cta-secondary">Read discussions ↓</a>
          <Link to="/register" className="public-hero-cta">Join free →</Link>
        </div>
        <p style={{ fontSize: '13px', color: '#e9d5ff', marginTop: '12px' }}>No real name required. Anonymous by default. <Link to="/welcome" style={{ color: '#fff', textDecoration: 'underline' }}>Learn how we keep you safe →</Link></p>
      </div>
      )}

      {/* ── How it works (visitors only) ── */}
      {!user && (
      <div className="hp-how-section">
        <div className="hp-how-grid">
          <div className="hp-how-card">
            <span className="hp-how-icon" aria-hidden="true">💬</span>
            <div className="hp-how-title">Share anonymously</div>
            <div className="hp-how-desc">Post about your experience — ADHD, anxiety, chronic pain, cultural identity. Everything is anonymous by default.</div>
          </div>
          <div className="hp-how-card">
            <span className="hp-how-icon" aria-hidden="true">💜</span>
            <div className="hp-how-title">"This resonates"</div>
            <div className="hp-how-desc">Find someone whose post speaks to you. Tap connect — no awkward intro needed, just pick a pre-written message.</div>
          </div>
          <div className="hp-how-card">
            <span className="hp-how-icon" aria-hidden="true">🤝</span>
            <div className="hp-how-title">Connect safely</div>
            <div className="hp-how-desc">Both sides agree to reveal more, one level at a time. Anonymous → names → full profiles → contact info. You control every step.</div>
          </div>
          <div className="hp-how-card">
            <span className="hp-how-icon" aria-hidden="true">🌟</span>
            <div className="hp-how-title">Real relationship</div>
            <div className="hp-how-desc">Message, meet, support each other. What starts as an anonymous post becomes a genuine connection with someone who truly gets it.</div>
          </div>
        </div>
      </div>
      )}

      {/* ── Trust banner ── */}
      <div className="public-anon-banner" role="banner">
        <span className="anon-icon" aria-hidden="true">🛡️</span>
        <span>Everything below is anonymous. Read freely — no account needed.</span>
      </div>

      {/* ── Communities ── */}
      <main className="pb-browse-container" id="communities">
        <div className="hp-section-header">
          <h2>Explore spaces</h2>
          <p className="hp-section-sub">
            {totalCommunities > 0 && `${totalCommunities} spaces`}
            {totalPosts > 0 && ` · ${totalPosts} anonymous posts`}
            {totalCommunities === 0 && 'Loading...'}
          </p>
        </div>

        {!loading && categoryGroups.length > 0 && (
          <div className="hp-search-bar">
            <span className="hp-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search spaces... (e.g. ADHD, anxiety, chronic pain)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="hp-search-input"
              aria-label="Search spaces"
            />
            {searchTerm && (
              <button className="hp-search-clear" onClick={() => setSearchTerm('')} aria-label="Clear search">✕</button>
            )}
          </div>
        )}

        {loading ? (
          <div>
            {[1,2,3].map(i => (
              <div key={i} className="skeleton-card">
                <div className="skeleton-line skeleton-title"></div>
                <div className="skeleton-line skeleton-text"></div>
                <div className="skeleton-line skeleton-text-short"></div>
              </div>
            ))}
          </div>
        ) : categoryGroups.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon" aria-hidden="true">🌱</div>
            <div className="empty-state-title">Growing something new</div>
            <div className="empty-state-text">Discussions are being prepared. Check back soon — new spaces are added regularly.</div>
          </div>
        ) : (
          <>
          {searchResults.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '16px', color: '#374151', marginBottom: '12px' }}>
                Posts matching &ldquo;{searchTerm}&rdquo; ({searchResults.length})
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {searchResults.map(post => (
                  <Link
                    key={post.post_id}
                    to={`/b/${post.channel_slug}`}
                    style={{ display: 'block', padding: '14px 16px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', textDecoration: 'none', color: 'inherit' }}
                  >
                    {post.title && <div style={{ fontWeight: 600, fontSize: '15px', color: '#1f2937', marginBottom: '4px' }}>{post.title}</div>}
                    <div style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5' }}>{post.content}</div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
                      {post.channel_name}{post.like_count > 0 ? ` · ${post.like_count} likes` : ''}{post.comment_count > 0 ? ` · ${post.comment_count} comments` : ''}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
          {filteredGroups.length === 0 && searchResults.length === 0 && searchTerm && (
            <div className="pb-browse-empty">No results for "{searchTerm}". Try a different term.</div>
          )}
          {filteredGroups.map(group => {
            const isOpen = openCategories[group.category_id];
            const groupPosts = group.channels.reduce((s, c) => s + c.post_count, 0);

            return (
              <div key={group.category_id} className="pb-cat-section">
                <button className="pb-cat-header" onClick={() => toggleCategory(group.category_id)} aria-expanded={isOpen}>
                  <div className="pb-cat-left">
                    <span className="pb-cat-icon" aria-hidden="true">{group.category_icon}</span>
                    <div>
                      <div className="pb-cat-title">{group.category_display}</div>
                      <div className="pb-cat-stats">
                        {group.channels.length} {group.channels.length === 1 ? 'space' : 'spaces'}
                        {groupPosts > 0 && <span className="pb-cat-dot">·</span>}
                        {groupPosts > 0 && `${groupPosts} posts`}
                      </div>
                    </div>
                  </div>
                  <span className={`pb-cat-chevron ${isOpen ? 'open' : ''}`}>›</span>
                </button>

                {isOpen && (
                  <div className="pb-cat-channels">
                    {group.channels.map(ch => (
                      <Link
                        key={ch.channel_id}
                        to={`/b/${ch.slug}`}
                        className="pb-ch-card"
                        onClick={() => trackEvent('public_channel_click', { channel: ch.slug })}
                      >
                        <div className="pb-ch-top">
                          <span className="pb-ch-slug">b/{ch.slug}</span>
                          {ch.post_count > 0 && <span className="pb-ch-badge">{ch.post_count} posts</span>}
                        </div>
                        <div className="pb-ch-name">{ch.name}</div>
                        <div className="pb-ch-meta">
                          {typeof ch.member_count === 'string' ? ch.member_count : `${ch.member_count} members`}
                          {ch.last_post_at && <><span className="pb-cat-dot">·</span>{formatTimeAgo(ch.last_post_at)}</>}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          </>
        )}
      </main>

      {/* ── Connection Stories (visitors only) ── */}
      {!user && (
      <div style={{ background: '#fff', padding: '48px 24px', borderTop: '1px solid #e8e5f0', borderBottom: '1px solid #e8e5f0' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <img 
            src="/images/undraw_coffee-with-friends_ocg2.svg" 
            alt="" 
            style={{ maxWidth: '200px', width: '100%', margin: '0 auto 24px', display: 'block', opacity: 0.85 }}
            onError={(e) => e.target.style.display = 'none'}
          />
          <h2 style={{ fontSize: '22px', marginBottom: '8px', textAlign: 'center' }}>Real connections that started here</h2>
          <p style={{ fontSize: '14px', color: '#999', textAlign: 'center', marginBottom: '24px' }}>Anonymized stories from Belong members</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
            <div style={{ background: '#f5f3ff', borderRadius: '12px', padding: '20px', border: '1px solid #ede9fe' }}>
              <p style={{ fontSize: '14px', color: '#555', lineHeight: '1.6', margin: '0 0 12px' }}>"I posted about living with ADHD and feeling like a fraud at work. Someone replied 'me too.' We connected, went through the disclosure levels, and now we meet weekly for accountability."</p>
              <div style={{ fontSize: '12px', color: '#7C3AED', fontWeight: '600' }}>— Two members from the ADHD space</div>
            </div>
            <div style={{ background: '#f0fdf4', borderRadius: '12px', padding: '20px', border: '1px solid #bbf7d0' }}>
              <p style={{ fontSize: '14px', color: '#555', lineHeight: '1.6', margin: '0 0 12px' }}>"I never talk about my chronic pain. On Belong I found someone in my city going through the same thing. We started anonymous, then shared names, then met for coffee. It changed everything."</p>
              <div style={{ fontSize: '12px', color: '#059669', fontWeight: '600' }}>— Two members from the Chronic Pain space</div>
            </div>
            <div style={{ background: '#fef3c7', borderRadius: '12px', padding: '20px', border: '1px solid #fde68a' }}>
              <p style={{ fontSize: '14px', color: '#555', lineHeight: '1.6', margin: '0 0 12px' }}>"As a first-gen immigrant, I felt caught between two worlds. On Belong I connected with three people who share that exact feeling. We went from anonymous to a group chat."</p>
              <div style={{ fontSize: '12px', color: '#d97706', fontWeight: '600' }}>— Members from the Cultural Identity space</div>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* ── What is Belong (visitors only) ── */}
      {!user && (
      <div className="hp-what-section">
        <div className="hp-what-inner">
          <img 
            src="/images/undraw_walking-together_2u0k.svg" 
            alt="" 
            style={{ maxWidth: '220px', width: '100%', margin: '0 auto 24px', display: 'block' }}
            onError={(e) => e.target.style.display = 'none'}
          />
          <h2>What is an invisible identity?</h2>
          <p>
            It's the part of who you are that deeply matters but isn't always visible.
            Mental health conditions. Neurodiversity. Chronic illness. Cultural background.
            Socioeconomic experience. Things you might not talk about at work or school —
            but that shape everything about how you experience the world.
          </p>
          <p>
            Belong is built for people like you. A place where you can be honest about
            what you're going through, find others who truly understand, and connect at your own pace.
          </p>
        </div>
      </div>
      )}

      {/* ── Email Subscribe (visitors only) ── */}
      {!user && (
        <div className="email-subscribe-section">
          <EmailSubscribeBox variant="general" />
        </div>
      )}

      {/* ── Final CTA (visitors only) ── */}
      {!user && (
      <div className="public-channel-cta">
        <h3>Ready to find your people?</h3>
        <p>Join Belong to post, comment, and connect with people who share your experience. Free forever. Anonymous by default.</p>
        <Link to="/register" className="public-cta-btn">Create your anonymous account</Link>
        <div className="hp-cta-sub">No credit card. No real name required. Takes 2 minutes.</div>
      </div>
      )}

      {/* ── Footer ── */}
      <footer className="public-channel-footer">
        <p>💜 Built with the belief that everyone deserves a place where they truly fit in.</p>
        <div className="public-footer-links">
          <Link to="/welcome">About Belong</Link>
          <Link to="/faq">FAQ</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/about">About Us</Link>
          <Link to="/help">Help</Link>
        </div>
        <div className="hp-footer-copy">© 2026 Belong. All rights reserved. Austin, TX.</div>
      </footer>

      {/* ── AI Companion (anonymous visitors only) ── */}
      {!user && <AiCompanion />}
    </div>
  );
}

export default PublicChannelBrowse;
